import { FormEvent } from "react"
import { router, useForm, usePage } from "@inertiajs/react"
import { Banknote, Check, CreditCard, Smartphone } from "lucide-react"
import { StaffLayout } from "@/components/layouts/staff-layout"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { MainContainer } from "@/components/ui/container"
import { FieldError, errorMessage } from "@/components/ui/field-error"
import { PageHeader } from "@/components/ui/headers"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Textarea } from "@/components/ui/textarea"
import { formatDate, formatMoney } from "@/lib/formatters"
import { cn } from "@/lib/utils"
import { t } from "@/lib/i18n"
type PaymentMethods = {
    cash: boolean
    physical_card_terminal: boolean
    digital_in_app: boolean
}
type StripeConnectStatus = {
    status: string
    ready: boolean
    providerAccountId: string | null
    chargesEnabled: boolean
    payoutsEnabled: boolean
    detailsSubmitted: boolean
    requirementsCurrentlyDue: string[]
    requirementsPastDue: string[]
    disabledReason: string | null
    requiredLocalFields: string[]
    hasStripeHostedRequirements: boolean
}
type MerchantProfile = {
    businessType: "company" | "individual"
    businessName: string | null
    legalBusinessName: string | null
    supportEmail: string | null
    supportPhone: string | null
    statementDescriptor: string | null
    businessUrl: string | null
    productDescription: string | null
    addressLine1: string | null
    addressLine2: string | null
    addressCity: string | null
    addressState: string | null
    addressPostalCode: string | null
    addressCountry: string | null
    representativeFirstName: string | null
    representativeLastName: string | null
    representativeEmail: string | null
    representativePhone: string | null
}
type BillingSettings = {
    id: number
    name: string
    status: string
    statusLabel: string
    paymentMethods: PaymentMethods
    paymentMethodsConfirmed: boolean
    stripeConnect: StripeConnectStatus
    pickupDinerFeePercentage: string
    pickupRestaurantFeePercentage: string
    operationallySuspended: boolean
    merchantProfile: MerchantProfile
    timeZone: string
}
type Props = {
    location: BillingSettings
    subscription: SubscriptionProps
    errors?: Record<string, string | string[]>
    flash?: {
        notice?: string
        alert?: string
    }
}
type BillingFormData = {
    payment_methods: PaymentMethods
    payment_methods_confirmed: boolean
    business_type: MerchantProfile["businessType"]
    business_name: string
    legal_business_name: string
    support_email: string
    statement_descriptor: string
    business_url: string
    product_description: string
    address_line1: string
    address_line2: string
    address_city: string
    address_state: string
    address_postal_code: string
    address_country: string
    representative_first_name: string
    representative_last_name: string
    representative_email: string
    representative_phone: string
}
type PaymentMethodMeta = {
    icon: typeof Banknote
    label: string
    hint: string
    requiresStripe: boolean
}
const PAYMENT_METHOD_META: Record<keyof PaymentMethods, PaymentMethodMeta> = {
    cash: {
        icon: Banknote,
        label: t("ui.payments.methods.long.cash"),
        hint: t("ui.react.pages.staff.location.manage.billing.settings.method_cash_hint"),
        requiresStripe: false,
    },
    physical_card_terminal: {
        icon: CreditCard,
        label: t("ui.payments.methods.long.physical_card_terminal"),
        hint: t("ui.react.pages.staff.location.manage.billing.settings.method_terminal_hint"),
        requiresStripe: false,
    },
    digital_in_app: {
        icon: Smartphone,
        label: t("ui.payments.methods.long.digital_in_app"),
        hint: t("ui.react.pages.staff.location.manage.billing.settings.method_digital_hint"),
        requiresStripe: true,
    },
}
function stripeStatusLabel(status: string) {
    switch (status) {
        case "ready":
            return t("ui.billing.stripe.ready_to_charge")
        case "restricted":
            return t("ui.billing.stripe.requires_action")
        case "disabled":
            return t("ui.billing.stripe.restricted")
        default:
            return t("ui.billing.stripe.unconfigured")
    }
}
function RequiredStripeFieldHint({ visible }: {
    visible: boolean
}) {
    if (!visible)
        return null
    return <p className="text-sm font-medium text-destructive">{t("ui.react.pages.staff.location.manage.billing.settings.field_required_continue_stripe")}</p>
}
type SubscriptionState = "unbilled" | "pending" | "current" | "delinquent" | "paused"

type SubscriptionProps = {
    state: SubscriptionState
    seated: boolean
    renewsAt: string | null
    lastCharge: { totalCents: number; taxCents: number; currency: string; paidAt: string | null; pdfUrl: string | null } | null
    branches: Array<{ id: number; name: string; monthlyCents: number }> | null
}

// Status tokens only, never a raw Tailwind hue, and the colour lives in the dot
// rather than the text. `paused` reads as healthy on purpose: somebody granted
// it deliberately, and a warning colour would send a manager chasing a problem
// that does not exist.
const SUBSCRIPTION_DOT: Record<SubscriptionState, string> = {
    unbilled: "bg-status-disabled",
    pending: "bg-warning",
    current: "bg-success",
    delinquent: "bg-destructive",
    paused: "bg-success",
}

function subscriptionKey(state: SubscriptionState, suffix: string) {
    return t(`ui.react.pages.staff.location.manage.billing.settings.subscription.${state}_${suffix}`)
}

function SubscriptionCard({ subscription, timeZone }: { subscription: SubscriptionProps; timeZone: string }) {
    const { state, seated, renewsAt, lastCharge, branches } = subscription
    // `seated` and not `state`. A group that already pays is `current`, and the
    // branch it has not bought a seat for still needs a button -- gating on the
    // group's state would leave that manager with nothing to click.
    const needsPayment = !seated

    return (<Card data-testid="subscription-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span className={cn("size-2 rounded-full", SUBSCRIPTION_DOT[state])} aria-hidden="true"/>
          {t("ui.react.pages.staff.location.manage.billing.settings.subscription.title")}
        </CardTitle>
        <CardDescription data-testid="subscription-state">{subscriptionKey(state, "description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {lastCharge ? (<div className="flex items-baseline justify-between gap-4">
            <span className="text-xs text-muted-foreground">
              {t("ui.react.pages.staff.location.manage.billing.settings.subscription.last_charge")}
            </span>
            <span className="text-foreground font-semibold tabular-nums" data-testid="subscription-last-charge">
              {formatMoney(lastCharge.totalCents / 100, { currency: lastCharge.currency.toUpperCase() })}
            </span>
          </div>) : null}

        {renewsAt ? (<div className="flex items-baseline justify-between gap-4">
            <span className="text-xs text-muted-foreground">
              {t("ui.react.pages.staff.location.manage.billing.settings.subscription.renews_at")}
            </span>
            <span className="text-foreground tabular-nums" data-testid="subscription-renews-at">
              {formatDate(renewsAt, { timeZone })}
            </span>
          </div>) : null}

        {/* Stripe's invoice says "Menny x 3" and cannot say which three. This is
            the breakdown it cannot carry, and it only appears for a group. */}
        {branches ? (<div className="space-y-1 border-t border-border pt-4">
            <p className="text-xs text-muted-foreground">
              {t("ui.react.pages.staff.location.manage.billing.settings.subscription.branches")}
            </p>
            {branches.map((branch) => (<div key={branch.id} className="flex items-baseline justify-between gap-4 text-sm">
                <span className="text-foreground">{branch.name}</span>
                <span className="text-foreground tabular-nums">
                  {formatMoney(branch.monthlyCents / 100, { currency: "MXN" })}
                </span>
              </div>))}
          </div>) : null}

        {needsPayment ? (<Button
            className="w-full"
            data-testid="subscription-subscribe"
            onClick={() => router.post("/manage/billing/subscribe")}
          >
            {state === "unbilled" || state === "delinquent"
                ? subscriptionKey(state, "cta")
                : t("ui.react.pages.staff.location.manage.billing.settings.subscription.add_seat_cta")}
          </Button>) : null}
      </CardContent>
    </Card>)
}

function PaymentMethodCard({ method, checked, needsStripe, onToggle }: {
    method: keyof PaymentMethods
    checked: boolean
    needsStripe: boolean
    onToggle: (checked: boolean) => void
}) {
    const meta = PAYMENT_METHOD_META[method]
    const Icon = meta.icon
    return (<label className={cn("flex cursor-pointer flex-col gap-3 rounded-lg border p-4 text-sm transition-colors", checked ? "border-primary bg-primary/5" : "hover:bg-muted/50")}>
      <div className="flex items-start justify-between gap-2">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-md border bg-card">
          <Icon className={cn("size-5", checked ? "text-primary" : "text-muted-foreground")}/>
        </span>
        <Checkbox aria-label={meta.label} checked={checked} onCheckedChange={(value) => onToggle(value === true)}/>
      </div>
      <div className="space-y-1">
        <p className="font-medium text-foreground">{meta.label}</p>
        <p className="text-xs text-muted-foreground">{meta.hint}</p>
      </div>
      <div className="mt-auto flex items-center gap-1.5 text-xs">

      </div>
      {needsStripe ? (<p className="text-xs text-warning">{t("ui.react.pages.staff.location.manage.billing.settings.requires_stripe_connection")}</p>) : null}
    </label>)
}
function ActivationStep({ label, done, current, detail }: {
    label: string
    done: boolean
    current: boolean
    detail?: string | null
}) {
    return (<li className="flex items-start gap-3">
      <span className={cn("mt-1.5 size-2 shrink-0 rounded-full", done ? "bg-status-available" : current ? "bg-status-occupied" : "bg-status-disabled/40")}/>
      <div className="min-w-0 flex-1">
        <p className={cn("text-sm", done ? "font-medium text-foreground" : "text-muted-foreground")}>{label}</p>
        {detail ? <p className="truncate text-xs text-muted-foreground">{detail}</p> : null}
      </div>
      {done ? <Check className="mt-0.5 size-4 shrink-0 text-status-available"/> : null}
    </li>)
}
export default function StaffLocationManageBillingSettings() {
    const { location, subscription, errors: serverErrors = {}, flash = {} } = usePage<Props>().props
    const initialFormData: BillingFormData = {
        payment_methods: location.paymentMethods,
        payment_methods_confirmed: location.paymentMethodsConfirmed,
        business_type: location.merchantProfile.businessType,
        business_name: location.merchantProfile.businessName ?? "",
        legal_business_name: location.merchantProfile.legalBusinessName ?? "",
        support_email: location.merchantProfile.supportEmail ?? "",
        statement_descriptor: location.merchantProfile.statementDescriptor ?? "",
        business_url: location.merchantProfile.businessUrl ?? "",
        product_description: location.merchantProfile.productDescription ?? "",
        address_line1: location.merchantProfile.addressLine1 ?? "",
        address_line2: location.merchantProfile.addressLine2 ?? "",
        address_city: location.merchantProfile.addressCity ?? "",
        address_state: location.merchantProfile.addressState ?? "",
        address_postal_code: location.merchantProfile.addressPostalCode ?? "",
        address_country: location.merchantProfile.addressCountry ?? "",
        representative_first_name: location.merchantProfile.representativeFirstName ?? "",
        representative_last_name: location.merchantProfile.representativeLastName ?? "",
        representative_email: location.merchantProfile.representativeEmail ?? "",
        representative_phone: location.merchantProfile.representativePhone ?? "",
    }
    const { data, setData, patch, transform, processing } = useForm(initialFormData)
    const requiredStripeFields = new Set(location.stripeConnect.requiredLocalFields)
    const stripeFieldNeedsAttention = (field: keyof BillingFormData) => {
        if (!requiredStripeFields.has(field))
            return false
        const value = data[field]
        if (typeof value === "string")
            return value.trim().length === 0
        if (typeof value === "boolean")
            return value === false
        return false
    }
    const fieldHasError = (field: keyof BillingFormData, error?: string | string[]) => Boolean(errorMessage(error)) || stripeFieldNeedsAttention(field)
    const stripeConnected = Boolean(location.stripeConnect.providerAccountId)
    const anyMethodEnabled = (Object.values(data.payment_methods) as boolean[]).some(Boolean)
    const profileComplete = stripeConnected && location.stripeConnect.requiredLocalFields.length === 0
    const activationSteps = [
        {
            label: t("ui.react.pages.staff.location.manage.billing.settings.activation_step_methods"),
            done: anyMethodEnabled,
            detail: null as string | null,
        },
        {
            label: t("ui.react.pages.staff.location.manage.billing.settings.activation_step_connected"),
            done: stripeConnected,
            detail: location.stripeConnect.providerAccountId,
        },
        {
            label: t("ui.react.pages.staff.location.manage.billing.settings.activation_step_profile"),
            done: profileComplete,
            detail: null as string | null,
        },
        {
            label: t("ui.react.pages.staff.location.manage.billing.settings.activation_step_ready"),
            done: location.stripeConnect.ready,
            detail: null as string | null,
        },
    ]
    const firstPendingIndex = activationSteps.findIndex((step) => !step.done)
    const refreshStatus = () => router.post("/manage/billing/refresh_stripe", {}, { preserveScroll: true })
    const submit = (event: FormEvent) => {
        event.preventDefault()
        transform((payload) => ({ location: payload }))
        patch("/manage/billing/settings", { preserveScroll: true })
    }
    return (<StaffLayout>
      <MainContainer size="md">
        <div className="space-y-6">
          <PageHeader title={t("ui.react.pages.staff.location.manage.billing.settings.settings_billing")} description={t("ui.react.pages.staff.location.manage.billing.settings.save_profile_payment_once_time")} actions={(<div className="flex items-center gap-2">
                <Badge variant={location.stripeConnect.ready ? "default" : "outline"}>
                  {stripeStatusLabel(location.stripeConnect.status)}
                </Badge>
                <Button asChild variant="outline" size="sm">
                  <a href="/manage/billing">{t("ui.react.pages.staff.location.manage.billing.settings.back_to_summary")}</a>
                </Button>
              </div>)}/>

          {flash.notice ? (<p className="rounded-lg border border-success/40 bg-success/10 p-3 text-sm text-success">
              {flash.notice}
            </p>) : null}

          {flash.alert ? (<p className="rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm text-warning">
              {flash.alert}
            </p>) : null}

          {location.operationallySuspended ? (<p className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">{t("ui.react.pages.staff.location.manage.billing.settings.location_suspended_missing_payment")}</p>) : null}

          <form onSubmit={submit}>
            <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px] xl:items-start">
              <div className="space-y-6">
                <SubscriptionCard subscription={subscription} timeZone={location.timeZone}/>

                <Card>
                  <CardHeader>
                    <CardTitle>{t("ui.react.pages.staff.location.manage.billing.settings.methods_payment_enabled")}</CardTitle>
                    <CardDescription>{t("ui.react.pages.staff.location.manage.billing.settings.configuration_visible_payment_daily_location")}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-3 sm:grid-cols-3">
                      {(Object.keys(PAYMENT_METHOD_META) as Array<keyof PaymentMethods>).map((method) => {
            const needsStripe = PAYMENT_METHOD_META[method].requiresStripe && !location.stripeConnect.ready
            return (<PaymentMethodCard key={method} method={method} checked={data.payment_methods[method]} needsStripe={needsStripe} onToggle={(value) => setData("payment_methods", {
                    ...data.payment_methods,
                    [method]: value,
                })}/>)
        })}
                    </div>
                    <FieldError error={serverErrors.payment_methods}/>

                    <label className="flex items-center gap-2 rounded-lg border p-3 text-sm">
                      <Checkbox checked={data.payment_methods_confirmed} onCheckedChange={(checked) => setData("payment_methods_confirmed", checked === true)}/>
                      <span>{t("ui.react.pages.staff.location.manage.billing.settings.confirmed_visible_methods")}</span>
                    </label>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>{t("ui.react.pages.staff.location.manage.billing.settings.profile_payment")}</CardTitle>
                    <CardDescription>{t("ui.react.pages.staff.location.manage.billing.settings.information_use_prefillear_stripe_connect")}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {location.stripeConnect.disabledReason ? (<p className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">{t("ui.react.pages.staff.location.manage.billing.settings.restriction")}{location.stripeConnect.disabledReason}
                      </p>) : null}

                    {location.stripeConnect.hasStripeHostedRequirements ? (<p className="rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm text-warning">{t("ui.react.pages.staff.location.manage.billing.settings.some_data_additional_completed_directly")}</p>) : null}

                    <div className="space-y-3">
                      <Label>{t("ui.react.pages.staff.location.manage.billing.settings.type_business")}</Label>
                      <RadioGroup value={data.business_type} onValueChange={(value) => setData("business_type", value as "company" | "individual")} className={cn("grid gap-2 sm:grid-cols-2", fieldHasError("business_type", serverErrors.business_type) && "rounded-lg border border-destructive/30 bg-destructive/5 p-2")}>
                        {[
            { value: "company", label: t("ui.react.pages.staff.location.manage.billing.settings.company") },
            { value: "individual", label: t("ui.react.pages.staff.location.manage.billing.settings.person_physical") },
        ].map((option) => (<label key={option.value} className={cn("flex items-center gap-2 rounded-lg border p-3 text-sm", fieldHasError("business_type", serverErrors.business_type) && "border-destructive/40")}>
                            <RadioGroupItem value={option.value}/>
                            <span>{option.label}</span>
                          </label>))}
                      </RadioGroup>
                      <RequiredStripeFieldHint visible={stripeFieldNeedsAttention("business_type") && !errorMessage(serverErrors.business_type)}/>
                      <FieldError error={serverErrors.business_type}/>
                    </div>

                    <div className="grid gap-4 lg:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="business_name">{t("ui.react.pages.staff.location.manage.billing.settings.name_business")}</Label>
                        <Input id="business_name" aria-invalid={fieldHasError("business_name", serverErrors.business_name) || undefined} value={data.business_name} onChange={(event) => setData("business_name", event.target.value)}/>
                        <RequiredStripeFieldHint visible={stripeFieldNeedsAttention("business_name") && !errorMessage(serverErrors.business_name)}/>
                        <FieldError error={serverErrors.business_name}/>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="legal_business_name">{t("ui.react.pages.staff.location.manage.billing.settings.legal_name")}</Label>
                        <Input id="legal_business_name" aria-invalid={fieldHasError("legal_business_name", serverErrors.legal_business_name) || undefined} value={data.legal_business_name} onChange={(event) => setData("legal_business_name", event.target.value)}/>
                        <RequiredStripeFieldHint visible={stripeFieldNeedsAttention("legal_business_name") && !errorMessage(serverErrors.legal_business_name)}/>
                        <FieldError error={serverErrors.legal_business_name}/>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="support_email">{t("ui.react.pages.staff.location.manage.billing.settings.email_support")}</Label>
                        <Input id="support_email" type="email" aria-invalid={fieldHasError("support_email", serverErrors.support_email) || undefined} value={data.support_email} onChange={(event) => setData("support_email", event.target.value)}/>
                        <RequiredStripeFieldHint visible={stripeFieldNeedsAttention("support_email") && !errorMessage(serverErrors.support_email)}/>
                        <FieldError error={serverErrors.support_email}/>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="support_phone">{t("ui.react.pages.staff.location.manage.billing.settings.phone_support")}</Label>
                        <Input id="support_phone" value={location.merchantProfile.supportPhone ?? ""} readOnly disabled/>
                        <p className="text-xs text-muted-foreground">{t("ui.react.pages.staff.location.manage.billing.settings.support_phone_managed_operational")}{" "}
                          <a href="/manage/location" className="font-medium text-foreground underline underline-offset-2">{t("ui.react.pages.staff.location.manage.billing.settings.support_phone_edit_link")}</a>
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="statement_descriptor">{t("ui.react.pages.staff.location.manage.billing.settings.statement_descriptor")}</Label>
                        <Input id="statement_descriptor" maxLength={22} aria-invalid={fieldHasError("statement_descriptor", serverErrors.statement_descriptor) || undefined} value={data.statement_descriptor} onChange={(event) => setData("statement_descriptor", event.target.value.toUpperCase())}/>
                        <p className="text-xs text-muted-foreground">{t("ui.react.pages.staff.location.manage.billing.settings.max_22_characters_see")}</p>
                        <RequiredStripeFieldHint visible={stripeFieldNeedsAttention("statement_descriptor") && !errorMessage(serverErrors.statement_descriptor)}/>
                        <FieldError error={serverErrors.statement_descriptor}/>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="business_url">{t("ui.react.pages.staff.location.manage.billing.settings.site_web")}</Label>
                        <Input id="business_url" placeholder="https://tu-restaurante.mx" aria-invalid={fieldHasError("business_url", serverErrors.business_url) || undefined} value={data.business_url} onChange={(event) => setData("business_url", event.target.value)}/>
                        <RequiredStripeFieldHint visible={stripeFieldNeedsAttention("business_url") && !errorMessage(serverErrors.business_url)}/>
                        <FieldError error={serverErrors.business_url}/>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="product_description">{t("ui.react.pages.staff.location.manage.billing.settings.business_description")}</Label>
                      <Textarea id="product_description" aria-invalid={fieldHasError("product_description", serverErrors.product_description) || undefined} value={data.product_description} onChange={(event) => setData("product_description", event.target.value)} className="min-h-[90px]" placeholder={t("ui.react.pages.staff.location.manage.billing.settings.restaurant_casual_mexican_consumption_table")}/>
                      <RequiredStripeFieldHint visible={stripeFieldNeedsAttention("product_description") && !errorMessage(serverErrors.product_description)}/>
                      <FieldError error={serverErrors.product_description}/>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>{t("ui.react.pages.staff.location.manage.billing.settings.address_stripe")}</CardTitle>
                    <CardDescription>{t("ui.react.pages.staff.location.manage.billing.settings.use_address_structured_text_free")}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 lg:grid-cols-2">
                      <div className="space-y-2 lg:col-span-2">
                        <Label htmlFor="address_line1">{t("ui.react.pages.staff.location.manage.billing.settings.street_number")}</Label>
                        <Input id="address_line1" aria-invalid={fieldHasError("address_line1", serverErrors.address_line1) || undefined} value={data.address_line1} onChange={(event) => setData("address_line1", event.target.value)}/>
                        <RequiredStripeFieldHint visible={stripeFieldNeedsAttention("address_line1") && !errorMessage(serverErrors.address_line1)}/>
                        <FieldError error={serverErrors.address_line1}/>
                      </div>
                      <div className="space-y-2 lg:col-span-2">
                        <Label htmlFor="address_line2">{t("ui.react.pages.staff.location.manage.billing.settings.extra")}</Label>
                        <Input id="address_line2" aria-invalid={fieldHasError("address_line2", serverErrors.address_line2) || undefined} value={data.address_line2} onChange={(event) => setData("address_line2", event.target.value)}/>
                        <RequiredStripeFieldHint visible={stripeFieldNeedsAttention("address_line2") && !errorMessage(serverErrors.address_line2)}/>
                        <FieldError error={serverErrors.address_line2}/>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="address_city">{t("ui.react.pages.staff.location.manage.billing.settings.city")}</Label>
                        <Input id="address_city" aria-invalid={fieldHasError("address_city", serverErrors.address_city) || undefined} value={data.address_city} onChange={(event) => setData("address_city", event.target.value)}/>
                        <RequiredStripeFieldHint visible={stripeFieldNeedsAttention("address_city") && !errorMessage(serverErrors.address_city)}/>
                        <FieldError error={serverErrors.address_city}/>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="address_state">{t("ui.react.pages.staff.location.manage.billing.settings.status")}</Label>
                        <Input id="address_state" aria-invalid={fieldHasError("address_state", serverErrors.address_state) || undefined} value={data.address_state} onChange={(event) => setData("address_state", event.target.value)}/>
                        <RequiredStripeFieldHint visible={stripeFieldNeedsAttention("address_state") && !errorMessage(serverErrors.address_state)}/>
                        <FieldError error={serverErrors.address_state}/>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="address_postal_code">{t("ui.react.pages.staff.location.manage.billing.settings.code_postal")}</Label>
                        <Input id="address_postal_code" aria-invalid={fieldHasError("address_postal_code", serverErrors.address_postal_code) || undefined} value={data.address_postal_code} onChange={(event) => setData("address_postal_code", event.target.value)}/>
                        <RequiredStripeFieldHint visible={stripeFieldNeedsAttention("address_postal_code") && !errorMessage(serverErrors.address_postal_code)}/>
                        <FieldError error={serverErrors.address_postal_code}/>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="address_country">{t("ui.react.pages.staff.location.manage.billing.settings.country")}</Label>
                        <Input id="address_country" aria-invalid={fieldHasError("address_country", serverErrors.address_country) || undefined} value={data.address_country} onChange={(event) => setData("address_country", event.target.value.toUpperCase())}/>
                        <RequiredStripeFieldHint visible={stripeFieldNeedsAttention("address_country") && !errorMessage(serverErrors.address_country)}/>
                        <FieldError error={serverErrors.address_country}/>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>{t("ui.react.pages.staff.location.manage.billing.settings.representative")}</CardTitle>
                    <CardDescription>{t("ui.react.pages.staff.location.manage.billing.settings.yes_company_use_data_prefillear")}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 lg:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="representative_first_name">{t("ui.react.pages.staff.location.manage.billing.settings.name")}</Label>
                        <Input id="representative_first_name" aria-invalid={fieldHasError("representative_first_name", serverErrors.representative_first_name) || undefined} value={data.representative_first_name} onChange={(event) => setData("representative_first_name", event.target.value)}/>
                        <RequiredStripeFieldHint visible={stripeFieldNeedsAttention("representative_first_name") && !errorMessage(serverErrors.representative_first_name)}/>
                        <FieldError error={serverErrors.representative_first_name}/>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="representative_last_name">{t("ui.react.pages.staff.location.manage.billing.settings.last_name")}</Label>
                        <Input id="representative_last_name" aria-invalid={fieldHasError("representative_last_name", serverErrors.representative_last_name) || undefined} value={data.representative_last_name} onChange={(event) => setData("representative_last_name", event.target.value)}/>
                        <RequiredStripeFieldHint visible={stripeFieldNeedsAttention("representative_last_name") && !errorMessage(serverErrors.representative_last_name)}/>
                        <FieldError error={serverErrors.representative_last_name}/>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="representative_email">{t("ui.react.pages.staff.location.manage.billing.settings.email")}</Label>
                        <Input id="representative_email" type="email" aria-invalid={fieldHasError("representative_email", serverErrors.representative_email) || undefined} value={data.representative_email} onChange={(event) => setData("representative_email", event.target.value)}/>
                        <RequiredStripeFieldHint visible={stripeFieldNeedsAttention("representative_email") && !errorMessage(serverErrors.representative_email)}/>
                        <FieldError error={serverErrors.representative_email}/>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="representative_phone">{t("ui.react.pages.staff.location.manage.billing.settings.phone")}</Label>
                        <Input id="representative_phone" aria-invalid={fieldHasError("representative_phone", serverErrors.representative_phone) || undefined} value={data.representative_phone} onChange={(event) => setData("representative_phone", event.target.value)}/>
                        <RequiredStripeFieldHint visible={stripeFieldNeedsAttention("representative_phone") && !errorMessage(serverErrors.representative_phone)}/>
                        <FieldError error={serverErrors.representative_phone}/>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <aside className="space-y-4 xl:sticky xl:top-16">
                <Card>
                  <CardHeader>
                    <CardTitle>{t("ui.react.pages.staff.location.manage.billing.settings.activation_title")}</CardTitle>
                    <CardDescription>{t("ui.react.pages.staff.location.manage.billing.settings.activation_hint")}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <ol className="space-y-3">
                      {activationSteps.map((step, index) => (<ActivationStep key={step.label} label={step.label} done={step.done} current={index === firstPendingIndex} detail={step.detail}/>))}
                    </ol>

                    <div className="flex flex-col gap-2">
                      <Button type="submit" disabled={processing}>{t("ui.react.pages.staff.location.manage.billing.settings.save_settings")}</Button>
                      {!location.stripeConnect.ready ? (<Button asChild type="button" variant="outline">
                          <a href="/manage/billing/stripe_connect">
                            {stripeConnected ? t("ui.react.pages.staff.location.manage.billing.settings.continue_onboarding") : t("ui.react.pages.staff.location.manage.billing.settings.connect_stripe")}
                          </a>
                        </Button>) : null}
                      {stripeConnected && !location.stripeConnect.ready ? (<Button type="button" variant="outline" onClick={refreshStatus}>{t("ui.react.pages.staff.location.manage.billing.settings.refresh_status")}</Button>) : null}
                      <Button asChild type="button" variant="ghost">
                        <a href="/manage/billing">{t("ui.react.pages.staff.location.manage.billing.settings.cancel")}</a>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </aside>
            </div>
          </form>

        </div>
      </MainContainer>
    </StaffLayout>)
}
